import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TuneIcon from "@mui/icons-material/Tune";
import BarChartIcon from "@mui/icons-material/BarChart";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useState } from "react";
import { withTranslation } from "react-i18next";
import { isNil } from "lodash";

import GridUtils from "@plannel/components/aggrid/GridUtils";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import { FS, FONT_FAMILY_CSS } from "@plannel/utils/font-util";
import { VersionCommonModal } from "@plannel/components/modal/VersionCommonModal";

const itemType = CodeUtil.itemType;
const ABCClassConfig = CodeUtil.ABCClassConfig;

// ─────────────────────────────────────────────
// 헬퍼 함수
// ─────────────────────────────────────────────

const makeActualPeriod = (classification) => {
  const { actualPeriodVal, actualPeriodEnd } = classification;
  if (actualPeriodVal && actualPeriodEnd) {
    const endDate = new ZDate(actualPeriodEnd);
    const startDate = new ZDate(endDate);
    startDate.setMonth(endDate.getMonth() - actualPeriodVal);
    const formatDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return `${formatDate(startDate)} ~ ${formatDate(endDate)}`;
  }
  return "-";
};

const makeDemandPeriod = (classification) => {
  const { demandPeriodVal, demandPeriodStart } = classification;
  if (demandPeriodVal && demandPeriodStart) {
    const startDate = new ZDate(demandPeriodStart);
    const endDate = new ZDate(startDate);
    endDate.setMonth(startDate.getMonth() + demandPeriodVal - 1);
    const formatDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return `${formatDate(startDate)} ~ ${formatDate(endDate)}`;
  }
  return "-";
};

const getItemTypeLabel = (classifiObj) => {
  const val = classifiObj?.targetItemType;
  if (isNil(val)) return null;
  const name = itemType?.[val]?.name;
  if (isNil(name)) return GridUtils.gridValueL10N(val);
  return GridUtils.gridValueL10N(name);
};


// ─────────────────────────────────────────────
// sections 빌더
// ─────────────────────────────────────────────

const buildSections = (classificationData, t) => {
  if (isNil(classificationData)) return [];
  const { classifiObj } = classificationData;

  return [
    {
      title: t("planInfo"),
      icon: TuneIcon,
      items: [
        { type: "text", label: t("classificationName"), value: classificationData.name },
        {
          type: "chip",
          label: t("status"),
          value: CodeUtil.versionStatusLabel(classificationData.statusCd),
          tintColor: ColorUtil.versionStatusColor(classificationData.statusCd),
        },
        { type: "text", label: t("classifiDimCd"), value: GridUtils.gridValueL10N(classificationData.classifiDimCd) },
      ],
    },
    {
      title: t("period"),
      icon: CalendarMonthIcon,
      items: [
        { type: "text", label: t("periodUom"),                          value: t(classificationData.periodUom) },
        { type: "text", label: `(${t("actual")}) ${t("numOfMonths")}`, value: classificationData.actualPeriodVal || "-" },
        { type: "text", label: `(${t("actual")}) ${t("period")}`,      value: makeActualPeriod(classificationData) },
        { type: "text", label: `(${t("demand")}) ${t("numOfMonths")}`, value: classificationData.demandPeriodVal || "-" },
        { type: "text", label: `(${t("demand")}) ${t("period")}`,      value: makeDemandPeriod(classificationData) },
      ],
    },
    classifiObj
      ? {
          title: t("policy"),
          icon: BarChartIcon,
          items: [
            { type: "text",     label: t("base"),           value: GridUtils.gridValueL10N(classifiObj.base) },
            { type: "text",     label: `XYZ ${t("base")}`, value: GridUtils.gridValueL10N(classifiObj.xyzCalcType) },
            { type: "checkbox", label: t("plant"),          value: classifiObj.plantLocAnalysis === true },
            { type: "text",     label: t("actualType"),     value: GridUtils.gridValueL10N(classifiObj.actualType) },
            { type: "text",     label: t("targetItemType"), value: getItemTypeLabel(classifiObj) },
            {
              type: "chipGroup",
              label: "ABC",
              items: [
                { label: "A", value: classifiObj.aClass, color: ABCClassConfig["A"]?.style?.backgroundColor },
                { label: "B", value: classifiObj.bClass, color: ABCClassConfig["B"]?.style?.backgroundColor },
                { label: "C", value: classifiObj.cClass, color: ABCClassConfig["C"]?.style?.backgroundColor },
              ],
            },
            {
              type: "chipGroup",
              label: "XYZ",
              items: [
                { label: "X", value: classifiObj.xClass, color: ABCClassConfig["X"]?.style?.backgroundColor },
                { label: "Y", value: classifiObj.yClass, color: ABCClassConfig["Y"]?.style?.backgroundColor },
                { label: "Z", value: classifiObj.zClass, color: ABCClassConfig["Z"]?.style?.backgroundColor },
              ],
            },
          ],
        }
      : null,
  ];
};

// ─────────────────────────────────────────────
// 메인 컴포넌트
// ─────────────────────────────────────────────

/**
 * Props:
 *
 *  [공통]
 *   t                   {Function}           i18n 번역 함수
 *
 *  [모드 선택]
 *   isCellRenderer      {boolean}            true  → ag-grid cellRenderer 모드 (셀에 텍스트 + 아이콘)
 *                                            false → 독립 모달 모드 (기본값)
 *
 *  [cellRenderer 모드 전용]  isCellRenderer={true} 일 때 ag-grid 가 자동으로 주입
 *   value               {object|string}      ag-grid field 값
 *                                              - classificationDto(객체) 또는 name(문자열)
 *   data                {object}             ag-grid row 전체 데이터 (value 가 문자열일 때 사용)
 *   icon                {SvgIconComponent}   셀에 표시할 아이콘 컴포넌트 (기본: InfoOutlinedIcon)
 *   componentIconStyle  {object}             아이콘 버튼에 추가할 sx 스타일
 *
 *  [독립 모달 모드 전용]  isCellRenderer={false} 일 때
 *   open                {boolean}            모달 open 상태
 *   onClose             {Function}           모달 닫기 핸들러
 *   classification      {object}             표시할 classification 데이터
 *
 * 사용 예시:
 *   // 독립 모달 (기본)
 *   <ClassificationSettingsModal open={open} onClose={handleClose} classification={data} />
 *
 *   // ag-grid cellRenderer
 *   { cellRenderer: ClassificationSettingsModal,
 *     cellRendererParams: { isCellRenderer: true, icon: SettingsIcon, componentIconStyle: { color: "red" } } }
 */
const ClassificationSettingsModal = ({
  t,
  // 모드 선택 [Grid cellRenderer 모드 / 독립 모달 모드]
  isCellRenderer = false,
  // cellRenderer 모드 props (ag-grid 자동 주입)
  value,
  data,
  icon: IconComponent = InfoOutlinedIcon,
  componentIconStyle,
  // 독립 모달 모드 props
  open = false,
  onClose,
  classification: externalClassification,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);

  // ── cellRenderer 모드 ──
  if (isCellRenderer) {
    // value 가 객체면 classificationDto, 아니면 row data(전체) 사용
    const classification =
      value !== null && value !== undefined && typeof value === "object" ? value : data;

    if (isNil(classification)) {
      return <Box sx={{ px: 1, lineHeight: "24px" }}>-</Box>;
    }

    return (
      <>
        {/* 셀 내용: 분류명 텍스트 + 아이콘 */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", height: "100%", px: 0.5 }}>
          <Typography
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              fontFamily: FONT_FAMILY_CSS,
              fontSize: FS.SUB,
            }}
          >
            {classification.name}
          </Typography>
          <IconButton
            size="small"
            color="info"
            sx={{ fontSize: FS.H1, ml: 0.5, p: 0.2, ...componentIconStyle }}
            onClick={(e) => {
              e.stopPropagation();
              setInternalOpen(true);
            }}
          >
            <IconComponent fontSize="inherit" />
          </IconButton>
        </Box>

        <VersionCommonModal
          open={internalOpen}
          onClose={() => setInternalOpen(false)}
          title={t("abcXyzClassification")}
          sections={buildSections(classification, t)}
        />
      </>
    );
  }

  // ── 독립 모달 모드 ──
  if (isNil(externalClassification)) return null;

  return (
    <VersionCommonModal
      open={open}
      onClose={onClose}
      title={t("abcXyzClassification")}
      sections={buildSections(externalClassification, t)}
    />
  );
};

// ag-grid cellRenderer 로 사용되는 컴포넌트이므로 DefaultGridSetting 에서
// 일반 함수로 직접 호출되지 않도록 식별 플래그를 부착합니다.
ClassificationSettingsModal.isReactCellRenderer = true;

export default withTranslation()(ClassificationSettingsModal);
